import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation, useParams } from "react-router";
import type { ActorDTO, TaskDTO } from "@roomate/core";
import { ApiError, usePinLesson, useActors, useMe, useProject, useProjectLessons, useSetLessonRetired } from "../api.ts";
import { ProjectArchivedNotice } from "../components/ProjectArchivedNotice.tsx";
import { Action, Page, PageHeader, Pill, Tile, Bento } from "../calm.tsx";
import { KIND_LABEL, filterLessonsBySource, isCorrectionLesson, lessonAnchor, lessonRef, lessonScope, partitionLessons, type LessonDTO, type LessonSourceFilter } from "../lessons.ts";
import { Ago, ActorName, cx, ErrorNote } from "../ui.tsx";

export function LessonsPage() {
  const { id = "" } = useParams();
  const project = useProject(id);
  const lessons = useProjectLessons(id);
  const actors = useActors().data;
  const { hash } = useLocation();
  const loaded = !!lessons.data;
  const [source, setSource] = useState<LessonSourceFilter>("all");

  // The run page links to #lesson-<id>. The browser only scrolls to a hash it finds on first paint,
  // and these rows arrive after the fetch, so scroll once they exist.
  useEffect(() => {
    if (loaded && hash) document.getElementById(hash.slice(1))?.scrollIntoView({ block: "center" });
  }, [loaded, hash]);

  if (lessons.error instanceof ApiError && lessons.error.status === 404)
    return (
      <Page>
        <p className="text-ink-3">Lessons are not available on this hub yet.</p>
      </Page>
    );
  if (lessons.error) return <Page><ErrorNote error={lessons.error} /></Page>;
  if (!lessons.data)
    return (
      <Page>
        <p className="text-ink-3">Loading…</p>
      </Page>
    );

  const all = lessons.data;
  const { active, retired } = partitionLessons(filterLessonsBySource(all, source));
  const tasks = project.data?.tasks ?? [];
  const projectName = project.data?.status.project.name ?? "Project";

  return (
    <Page wide>
      <PageHeader
        crumbs={[
          { label: "Overview", to: "/" },
          { label: projectName, to: `/projects/${id}` },
        ]}
        title="Lessons"
        meta={<span className="max-w-[64ch] text-ink-2">Short notes agents wrote about traps and fixes in this project. Active lessons go to later runs whose task touches the same areas.</span>}
      />
      <ProjectArchivedNotice projectId={id} />

      {all.length === 0 ? (
        <Bento className="mt-8">
          <Tile span={6} title="No lessons yet" testId="lessons-empty">
            <div className="space-y-3 text-[14px] leading-relaxed text-ink-2">
              <p>
                A lesson is a one-line note an agent records when a run learned something non-obvious: a suite that only goes red under load, a push that never
                landed, a shell trap that cost a retry.
              </p>
              <p>
                Agents write one only when they have something to say, so an empty list is normal. When a lesson is active, later runs on overlapping areas read it as an
                advisory note, and a person can retire any lesson that turns out to be wrong.
              </p>
            </div>
          </Tile>
        </Bento>
      ) : (
        <>
          <SourceFilter value={source} onChange={setSource} />
          <LessonSection
            title="Active"
            testId="lessons-active"
            lessons={active}
            emptyText={source === "all" ? "No active lessons. Retired ones are listed below." : "No active lessons from this source."}
            render={(l) => <LessonRow key={l.id} lesson={l} all={all} tasks={tasks} actors={actors} />}
          />
          <LessonSection
            title="Retired"
            testId="lessons-retired"
            lessons={retired}
            emptyText="Nothing retired. A lesson a person retires stays here, and can be restored."
            render={(l) => <LessonRow key={l.id} lesson={l} all={all} tasks={tasks} actors={actors} />}
          />
        </>
      )}
    </Page>
  );
}

const SOURCE_FILTERS: { value: LessonSourceFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "evidence", label: "From evidence" },
  { value: "correction", label: "From corrections" },
];

function SourceFilter({ value, onChange }: { value: LessonSourceFilter; onChange: (v: LessonSourceFilter) => void }) {
  return (
    <div className="mt-6 inline-flex gap-1 rounded-full bg-ink/[0.05] p-1" role="group" aria-label="Filter lessons by source" data-testid="lessons-source-filter">
      {SOURCE_FILTERS.map((f) => (
        <button
          key={f.value}
          type="button"
          aria-pressed={value === f.value}
          data-testid={`lessons-source-${f.value}`}
          onClick={() => onChange(f.value)}
          className={cx("rounded-full px-3 py-1 text-[13px] transition-colors", value === f.value ? "bg-paper text-ink shadow-sm" : "text-ink-2 hover:text-ink")}
        >
          {f.label}
        </button>
      ))}
    </div>
  );
}

function LessonSection({
  title,
  testId,
  lessons,
  emptyText,
  render,
}: {
  title: string;
  testId: string;
  lessons: LessonDTO[];
  emptyText: string;
  render: (lesson: LessonDTO) => ReactNode;
}) {
  return (
    <section className="mt-8" data-testid={testId} aria-label={`${title} lessons`}>
      <h2 className="mb-2 flex items-baseline gap-2 text-[13px] font-medium text-ink-2">
        {title} <span className="text-ink-3">{lessons.length}</span>
      </h2>
      {lessons.length === 0 ? (
        <p className="px-1 py-3 text-[13px] italic text-ink-3">{emptyText}</p>
      ) : (
        <ul className="divide-y divide-rule/60">{lessons.map((l) => render(l))}</ul>
      )}
    </section>
  );
}

function LessonRow({ lesson, all, tasks, actors }: { lesson: LessonDTO; all: LessonDTO[]; tasks: TaskDTO[]; actors: ActorDTO[] | undefined }) {
  // Each row owns its mutation, so a pending state or an error belongs to that row alone.
  const retire = useSetLessonRetired();
  const me = useMe().data;
  // Retire and restore are people only, and a viewer cannot write (spec §2.6).
  const canChange = me?.kind === "human" && me.access !== "viewer";
  const source = lesson.taskId ? tasks.find((t) => t.id === lesson.taskId) : undefined;
  const agent = actors?.find((a) => a.id === lesson.createdBy);
  const survivor = lesson.mergedInto ? all.find((l) => l.id === lesson.mergedInto) : undefined;
  const pin = usePinLesson();
  const pending = retire.isPending;
  const retiring = lesson.status === "active";
  // Probation (dec_ejr11k8bjq): an active lesson is injected only once another task confirms it or a person pins it.
  const onProbation = lesson.status === "active" && !lesson.live;
  const pinnable = onProbation && canChange;
  const retiredBy = lesson.retiredBy ? actors?.find((a) => a.id === lesson.retiredBy)?.name ?? lesson.retiredBy : null;

  return (
    <li id={lessonAnchor(lesson.id)} data-lesson-id={lesson.id} data-lesson-status={lesson.status} className="scroll-mt-24 py-4">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1">
          <p className="whitespace-pre-wrap break-words text-[14.5px] leading-relaxed">{lesson.text}</p>
          <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px] text-ink-3">
            <Pill tone={lesson.kind === "pitfall" ? "signal" : "neutral"}>{KIND_LABEL[lesson.kind]}</Pill>
            {isCorrectionLesson(lesson) && (
              <span className="rounded-full bg-ink/[0.06] px-2 py-0.5 text-[12px] text-ink-2" data-testid="lesson-correction-badge" title="Drafted from a review send-back">
                correction
              </span>
            )}
            {isCorrectionLesson(lesson) && (lesson.agentIds?.length ?? 0) > 0 && (
              <span className="text-ink-2" data-testid="lesson-agents">
                for{" "}
                {lesson.agentIds!.map((agentId, i) => (
                  <span key={agentId}>
                    {i > 0 && ", "}
                    <ActorName id={agentId} actors={actors} />
                  </span>
                ))}
              </span>
            )}
            {onProbation && (
              <span className="rounded-full bg-ink/[0.06] px-2 py-0.5 text-[12px] text-ink-2" data-testid="lesson-probation" title="Not injected into runs until another task confirms it or someone pins it">
                On probation
              </span>
            )}
            <span className="text-ink-2" data-testid="lesson-scope">
              {lessonScope(lesson.areas)}
            </span>
            <span aria-hidden>·</span>
            <span>
              confirmed {lesson.confirmations}×
            </span>
            <span aria-hidden>·</span>
            <span>
              injected into {lesson.injections} run{lesson.injections === 1 ? "" : "s"}
            </span>
          </p>
          <p className="mt-1 flex flex-wrap items-center gap-x-2 text-[12.5px] text-ink-3">
            {lesson.taskId ? (
              <Link to={`/tasks/${lesson.taskId}`} className="text-ink-2 underline decoration-rule hover:text-ink">
                {source ? `#${source.number} ${source.title}` : "source task"}
              </Link>
            ) : (
              <span>recorded outside a task</span>
            )}
            <span aria-hidden>·</span>
            <span>{agent?.name ?? "unknown agent"}</span>
            <span aria-hidden>·</span>
            <Ago ts={lesson.createdAt} />
            {lesson.status === "retired" && retiredBy && (
              <>
                <span aria-hidden>·</span>
                <span>retired by {retiredBy}</span>
              </>
            )}
            {lesson.mergedInto && (
              <>
                <span aria-hidden>·</span>
                <a href={`#${lessonAnchor(lesson.mergedInto)}`} className="text-ink-2 underline decoration-rule hover:text-ink" data-testid="lesson-merged-into">
                  merged into {lessonRef(lesson.mergedInto)}
                  {survivor && survivor.status === "retired" ? " (retired)" : ""}
                </a>
              </>
            )}
          </p>
        </div>
        {canChange && (
          <div className="flex shrink-0 flex-col items-end gap-1.5">
            {pinnable && (
              <Action testId="lesson-pin" disabled={pin.isPending} onClick={() => pin.mutate(lesson.id)}>
                {pin.isPending ? "Pinning…" : "Pin"}
              </Action>
            )}
            <Action
              testId={retiring ? "lesson-retire" : "lesson-restore"}
              danger={retiring}
              disabled={pending}
              onClick={() => retire.mutate({ id: lesson.id, retire: retiring })}
            >
              {pending ? (retiring ? "Retiring…" : "Restoring…") : retiring ? "Retire" : "Restore"}
            </Action>
            {retire.error && (
              <div className="max-w-[34ch] text-right">
                <ErrorNote error={retire.error} />
              </div>
            )}
            {pin.error && (
              <div className="max-w-[34ch] text-right">
                <ErrorNote error={pin.error} />
              </div>
            )}
          </div>
        )}
      </div>
    </li>
  );
}
